import { useCallback, useMemo, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { IssuesAlert } from './components/IssuesAlert';
import { MatchingStepper } from './components/MatchingStepper';
import { StepNav } from './components/StepNav';
import type { MatchingDraft } from './hooks/useMatchingDraft';
import { collectIssues, valuesByColumn } from './model/review';
import {
  SETUP_STEPS,
  type DataTab,
  type IssueTarget,
  type SetupStep,
} from './model/steps';
import type { MatchingSessionState } from './model/types';
import { SetupContext, type SetupContextValue } from './SetupContext';
import { ConfigureDataStep } from './steps/data/ConfigureDataStep';
import { RulesAndGoalsStep } from './steps/goals/RulesAndGoalsStep';
import { ReviewStep } from './steps/review/ReviewStep';
import { StartMatchingModal } from './steps/review/StartMatchingModal';

interface SetupFlowProps {
  session: MatchingSessionState;
  draft: MatchingDraft;
  send: (method: string, arg?: unknown) => boolean;
  /** After the run starts the flow is shown read-only. */
  readOnly?: boolean;
}

/** The 3-step setup: Configure data → Requirements and goals → Review and confirm. */
export function SetupFlow({
  session,
  draft,
  send,
  readOnly = false,
}: SetupFlowProps) {
  const [step, setStep] = useState<SetupStep>('data');
  const [dataTab, setDataTab] = useState<DataTab>('participants');
  const [goalColumn, setGoalColumn] = useState<string | null>(null);
  const [onlyUnrecognizedDnm, setOnlyUnrecognizedDnm] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [starting, setStarting] = useState(false);
  const { config, update, flush } = draft;

  const values = useMemo(() => valuesByColumn(session), [session]);
  const issues = useMemo(
    () => collectIssues(session, config, values),
    [session, config, values]
  );

  const goTo = useCallback(
    (target: IssueTarget) => {
      if (!readOnly) flush();
      setStep(target.step);
      if (target.dataTab) setDataTab(target.dataTab);
      if (target.goalColumn) setGoalColumn(target.goalColumn);
      if (target.onlyUnrecognizedDnm) setOnlyUnrecognizedDnm(true);
      window.scrollTo({ top: 0 });
    },
    [flush, readOnly]
  );

  const ctx: SetupContextValue = {
    session,
    config,
    update,
    values,
    issues,
    readOnly,
    goTo,
  };
  const index = SETUP_STEPS.findIndex(s => s.id === step);
  const pageIssues =
    step === 'review' ? issues : issues.filter(i => i.target.step === step);
  const unsaved = config.goals.filter(g => !g.saved).length;
  const blocking = issues.some(i => i.blocks);

  let nextDisabled = false;
  let hint: string | undefined;
  if (step === 'goals' && unsaved > 0) {
    nextDisabled = true;
    hint = `${unsaved} matching ${unsaved === 1 ? 'goal' : 'goals'} not saved yet`;
  }
  if (step === 'review' && (blocking || readOnly)) {
    nextDisabled = true;
    hint = readOnly ? undefined : 'Fix the issues at the top of the page first';
  }

  const start = () => {
    setStarting(true);
    // Send the latest config even if nothing changed, then start.
    if (flush(true) && send('start')) return;
    setStarting(false);
    notifications.show({
      color: 'red',
      title: "Couldn't start matching",
      message:
        "We're not connected to the matching service right now. Try again in a moment.",
    });
  };

  return (
    <SetupContext.Provider value={ctx}>
      <MatchingStepper
        step={step}
        issues={issues}
        onStepChange={s => goTo({ step: s })}
      />
      {!readOnly && (
        <IssuesAlert
          issues={pageIssues}
          onReview={step === 'review'}
          onFix={goTo}
        />
      )}

      {step === 'data' && (
        <ConfigureDataStep
          tab={dataTab}
          onTabChange={setDataTab}
          onlyUnrecognizedDnm={onlyUnrecognizedDnm}
          onOnlyUnrecognizedDnmChange={setOnlyUnrecognizedDnm}
        />
      )}
      {step === 'goals' && (
        <RulesAndGoalsStep
          goalColumn={goalColumn}
          onGoalColumnChange={setGoalColumn}
        />
      )}
      {step === 'review' && <ReviewStep />}

      <StepNav
        showBack={index > 0}
        nextLabel={SETUP_STEPS[index].next}
        nextDisabled={nextDisabled}
        hint={hint}
        onBack={() => goTo({ step: SETUP_STEPS[index - 1].id })}
        onNext={() =>
          step === 'review'
            ? setConfirming(true)
            : goTo({ step: SETUP_STEPS[index + 1].id })
        }
      />

      {confirming && (
        <StartMatchingModal
          requestName={config.request.name}
          goalCount={config.goals.length}
          starting={starting}
          onStart={start}
          onClose={() => setConfirming(false)}
        />
      )}
    </SetupContext.Provider>
  );
}
